import { cn } from "../../lib/cn.js";
import { istDateTime, istMinutesOfDay, istWeekday } from "../../lib/format.js";
import type { HealthResponse, PipelineHealth } from "../../lib/queries.js";
import { Badge } from "../ui/Badge.js";
import { Section } from "../ui/Section.js";

/** Long enough to identify the failure, short enough to sit in a card. */
const ERROR_CHARS = 160;

export interface KiteStatusCardProps {
  kite: HealthResponse["kite"];
  /** The kite-login pipeline row, which is what says whether this recovers. */
  login: PipelineHealth | undefined;
  /** The reader's clock, injected so the poll-window sentence is testable. */
  now?: Date;
}

/**
 * Token state, and the manual login one click away.
 *
 * The link is not decoration. A fallback nobody has run in six months is not
 * a fallback, and putting it on the page an operator already opens is what
 * keeps it exercised (spec §10, risk 5).
 *
 * When there is no session it also says whether one is coming. A revoked
 * token and a broken auto-login both read as "Expired" here, and only the
 * second one needs a human: the first is fifteen minutes from fixing itself,
 * as long as the login poll is running at all.
 */
export function KiteStatusCard({ kite, login, now = new Date() }: KiteStatusCardProps) {
  return (
    <Section title="Kite session">
      <p className="flex items-baseline gap-2 text-sm">
        <Badge tone={kite.valid ? "up" : "urgent"}>{kite.valid ? "Valid" : "Expired"}</Badge>
        <span className="text-text-muted">
          {kite.expiresAt ? `until ${istDateTime(kite.expiresAt)}` : "no token"}
        </span>
      </p>

      <dl className="flex flex-col gap-1 text-sm text-text-muted">
        <div className="flex justify-between gap-4">
          <dt>Obtained by</dt>
          <dd className="text-text">{kite.obtainedBy ?? "—"}</dd>
        </div>
        <div className="flex justify-between gap-4">
          <dt>Symbols with no instrument token</dt>
          <dd
            className={cn("tabular-nums", kite.unmappedSymbols > 0 ? "text-warning" : "text-text")}
          >
            {kite.unmappedSymbols}
          </dd>
        </div>
      </dl>

      {kite.valid ? null : <Recovery login={login} now={now} />}

      <a
        href="/api/kite/login"
        className="mt-2 self-start border border-border px-3 py-1 text-xs hover:border-accent hover:text-accent"
      >
        Log in manually
      </a>
    </Section>
  );
}

/**
 * The kite-login poll window, IST minutes past midnight.
 *
 * Mirrors the schedule in `apps/worker/src/jobs/kite-login.ts`. Duplicated
 * rather than served from the API, which would mean a Health field whose only
 * reader is one sentence on one card.
 */
const POLL_FROM = 6 * 60 + 15;
const POLL_TO = 15 * 60 + 45;

/**
 * Is a login attempt actually due in the next fifteen minutes?
 *
 * The clock and the weekend are both answerable here. The exchange holiday
 * calendar is not: it lives in `@savi/core`, which this app deliberately does
 * not depend on, and ten days a year does not buy a Node-shaped package in
 * the browser bundle. That is why the closed sentence names the rule instead
 * of naming a time.
 */
function polling(now: Date): boolean {
  const minutes = istMinutesOfDay(now);
  if (minutes < POLL_FROM || minutes > POLL_TO) return false;
  const weekday = istWeekday(now);
  return weekday >= 1 && weekday <= 5;
}

/**
 * Whether the missing session is recovering on its own.
 *
 * Two independent facts, so both get their own line. A failed row carries the
 * reason auto-login broke. The second line says whether anything is coming at
 * all: the poll runs every fifteen minutes from 06:15 to 15:45 IST on session
 * days, and at 18:00 or on a Sunday the next attempt is the next session
 * morning. Promising fifteen minutes there is the "needs a human" ambiguity
 * this card exists to remove.
 */
function Recovery({ login, now }: { login: PipelineHealth | undefined; now: Date }) {
  return (
    <>
      {login?.status === "failed" ? (
        <p className="text-xs text-danger" title={login.error ?? undefined}>
          Auto-login failed: {truncate(login.error)}
        </p>
      ) : null}
      {polling(now) ? (
        <p className="text-xs text-text-muted">Auto-login retries every 15 minutes.</p>
      ) : (
        <p className="text-xs text-text-muted">
          Auto-login runs 06:15 to 15:45 IST on session days. The next attempt is the next session
          morning.
        </p>
      )}
    </>
  );
}

function truncate(error: string | null): string {
  if (!error) return "no reason recorded";
  return error.length > ERROR_CHARS ? `${error.slice(0, ERROR_CHARS)}…` : error;
}
